import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
// Icons
import HistoryIcon from "@mui/icons-material/History";
import { DeleteOutlined as DeleteOutlineIcon } from "@mui/icons-material";
// Components
import { DeleteHistoryDialog } from "./DeleteHistory";
import { capitalizeWords } from "../../utils/functions";

export const UserHistoryPanel = ({
  history = [],
  onDeleteHistoryItem,
  rerunFromHistory,
  currentlyGenerating = false,
  currentHistoryId = null,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    itemId: null,
    itemTitle: "",
    isLoading: false,
  });

  const drawerWidth = 320;

  // Togle drawer open and close
  const toggleDrawer = () => {
    setOpen(!open);
  };

  // Format date
  const formatTime = (time) => {
    if (!time) return "No date";

    try {
      const date = typeof time === "string" ? new Date(time) : time;
      if (isNaN(date)) {
        return "Invalid date";
      }
      return date.toLocaleString("en-GB", {
        timeZone: "Europe/London",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "No date";
    }
  };

  // Create title
  const createTitle = (entry) => {
    try {
      const sector = entry?.sector || "Unknown Sector";
      const country = entry?.country || "Unknown Country";
      return `${sector} in ${country}`;
    } catch (error) {
      console.error("Error creating title: ", error);
      return "Unknown Entry";
    }
  };

  // Handle delete item and prevent triggering rerun function
  const handleDeleteHistoryItem = async (e, itemId) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      const historyItem = history.find((item) => item.id === itemId);
      const itemTitle = historyItem ? createTitle(historyItem) : "Unknown Item";

      // Category path
      const categoryLevels = [
        historyItem?.L1,
        historyItem?.L2,
        historyItem?.L3,
        historyItem?.L4,
      ].filter((level) => level && level.trim() !== "");
      const categoryPath = categoryLevels.join(" > ");

      setDeleteDialog({
        open: true,
        itemId: itemId,
        itemTitle: itemTitle,
        itemCategory: capitalizeWords(categoryPath),
        isLoading: false,
      });
    } catch (error) {
      console.log("Error handling delte history item: ", error);
    }
  };
  // Handle the actual deletion after confirmation
  const handleConfirmDelete = async () => {
    try {
      setDeleteDialog((prev) => ({ ...prev, isLoading: true }));

      if (onDeleteHistoryItem) {
        await onDeleteHistoryItem(deleteDialog.itemId);
      }

      // Close dialog
      setDeleteDialog({
        open: false,
        itemId: null,
        itemTitle: "",
        isLoading: false,
      });
    } catch (error) {
      console.error("Error deleting history item: ", error);
      setDeleteDialog((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Handle dialog close
  const handleCloseDeleteDialog = () => {
    if (!deleteDialog.isLoading) {
      setDeleteDialog({
        open: false,
        itemId: null,
        itemTitle: "",
        isLoading: false,
      });
    }
  };

  // Close drawer on small screen when selecting history item
  const handleHistoryItemClick = (entry) => {
    console.log("running entry", entry, currentHistoryId);

    try {
      // Don't allow clicking if current entry
      if (currentlyGenerating || entry.id === currentHistoryId) {
        return;
      }
      rerunFromHistory(entry);
      if (window.innerWidth < 600) {
        setOpen(false);
      }
    } catch (error) {
      console.error("Error handling history item click: ", error);
    }
  };

  const renderHistoryItems = (entry, index) => {
    try {
      // Category path L1>L2>L3>L4
      const categoryLevels = [entry.L1, entry.L2, entry.L3, entry.L4].filter(
        (level) => level && level.trim() !== "",
      );
      const categoryPath = categoryLevels.join(" > ");

      // Check if this history is current entry
      const isCurrentEntry = entry.id === currentHistoryId;
      const isClickable = !currentlyGenerating && !isCurrentEntry;
      return (
        <Paper
          key={entry.id || `fallback-${index}`}
          elevation={isCurrentEntry ? 0 : 1}
          sx={{
            p: 2,
            mb: 2,
            position: "relative",
            cursor: isClickable ? "pointer" : "default",
            backgroundColor: isCurrentEntry
              ? theme.palette.primary.background
              : "inherit",
            opacity: currentlyGenerating && !isCurrentEntry ? 0.6 : 1,
            border: isCurrentEntry
              ? `1px solid ${"#d3e3fe"}`
              : "1px solid transparent",
            "&:hover": isClickable
              ? { backgroundColor: theme.palette.primary.lightBackground }
              : {},
            transition: "all 0.2s ease-in-out",
          }}
          onClick={() => (isClickable ? handleHistoryItemClick(entry) : "")}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                display: "block",
                fontweight: isCurrentEntry ? 600 : 400,
                color: isCurrentEntry
                  ? theme.palette.gray.darker
                  : theme.palette.primary.main,
              }}
            >
              {createTitle(entry)}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: isCurrentEntry ? "text.secondary" : "inherit",
              }}
            >
              {capitalizeWords(categoryPath) || "Category path not available"}
            </Typography>

            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mt: 1 }}
            >
              {formatTime(entry.time)}
            </Typography>
          </Box>

          {!isCurrentEntry && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => handleDeleteHistoryItem(e, entry.id)}
                sx={{
                  position: "absolute",
                  bottom: 14,
                  right: 14,
                  color: "secondary.chilliRed",
                  backgroundColor: "primary.white",
                  boxShadow: "none",
                  "&:hover": {
                    color: "primary.white",
                    backgroundColor: "secondary.chilliRed",
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
      );
    } catch (error) {
      console.error("Error rendering history item: ", error);
      return (
        <Paper key={`error-${index}`}>
          <Typography variant="body2" color="error">
            Error loading history item
          </Typography>
        </Paper>
      );
    }
  };

  return (
    <>
      <Tooltip title="User History">
        <IconButton
          variant="outlined"
          size="large"
          onClick={toggleDrawer}
          sx={{
            backgroundColor: "primary.brandMain",
            color: "primary.main",
            boxShadow: `4px 4px 12px ${theme.palette.secondary.lightBlack}70`,
            "&:hover": {
              backgroundColor: "primary.main",
              color: "primary.brandMain",
              boxShadow: `4px 4px 12px ${theme.palette.primary.main}70`,
              opacity: 0.9,
            },
          }}
        >
          <HistoryIcon />
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        open={open}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            zIndex: theme.zIndex.drawer,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            overflowY: "auto",
            borderLeft: `1px solid ${theme.palette.divider}`,
            my: 1,
          }}
        >
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, pb: "4px", pt: "10px", px: 2 }}
            >
              User History
            </Typography>
          </Box>
          <Box sx={{ mx: 2 }}>
            {history.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                No history available
              </Typography>
            ) : (
              history.map((entry, index) => renderHistoryItems(entry, index))
            )}
          </Box>
        </Paper>
      </Drawer>
      <DeleteHistoryDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        historyTitle={deleteDialog.itemTitle}
        categoryPath={deleteDialog.itemCategory}
        isLoading={deleteDialog.isLoading}
      />
    </>
  );
};
